import { useEffect, useRef, useState } from 'react';
import type { OptionCardView, RelativeFacing } from '../api.js';
import { AIM_GLYPH, AIM_LABEL } from '../aimLabels.js';
import { payloadNote } from '../optionPayload.js';

/**
 * The Options a robot is holding, on its own tableau.
 *
 * Options are public — everyone can see what everyone holds — and every
 * robot's tray is this one, the viewer's included. Other robots used to
 * get look-alike chips that carried the card's text in a `title` and
 * nothing else, so the wording an opponent most needs to read, on a card
 * they have never held, was the one copy you could not open.
 *
 * Each card opens its own text. A player who has just been handed
 * "Fourth Gear" needs to be able to read what it does without leaving the
 * game, and the wording is the rule. The same goes for the player working
 * out what the Fourth Gear across the table is about to do to them.
 */

/**
 * What a device dropped by this Option is, in the rulebook's terms.
 *
 * Flat devices are not pushed by pushers or other robots and do not
 * affect flying robots; liquids and vapour behave differently again. The
 * tags matter in play, so they are worth stating alongside the timing.
 */
const DEVICE_TAGS: Record<string, string> = {
  mineLayer: 'Launcher, Flat Device',
  proximityMine: 'Launcher, Flat Device',
  spikeDropper: 'Launcher, Flat Device',
  gooDropper: 'Launcher, Flat Device',
  seismicCharge: 'Launcher, Flat Device',
  scramblerBomb: 'Launcher, Flat Device',
  portableTeleporter: 'Launcher, Flat Device',
  oilDropper: 'Launcher, Liquid',
  flamingOilDropper: 'Launcher, Liquid',
  waterDropper: 'Launcher, Liquid',
  smokeDropper: 'Launcher, Vapor',
};

function timingLabel(option: OptionCardView): string {
  const base = option.timing === 'runTime' ? 'Run-Time'
    : option.timing === 'mainLaser' ? 'Main Laser Modification'
      : option.timing === 'additionalWeapon' ? 'Additional Weapon'
        : option.timing === 'optionalWeapon' ? 'Optional Weapon'
          : option.timing === 'phaseProgrammed' ? 'Phase-Programmed'
            : option.timing === 'turnProgrammed' ? 'Turn-Programmed'
              : 'Run-Time (Mandatory)';
  const tags = DEVICE_TAGS[option.effect];
  return tags ? `${base}, ${tags}` : base;
}

export function OptionTray(
  { options, activated = [], aimed }:
  {
    options: OptionCardView[];
    activated?: string[];
    /**
     * Which way each aimed Option is turned this turn, or undefined while
     * the turn's programming is still secret.
     *
     * An empty LIST is not the same thing: an Option with no entry faces
     * front, so nothing turned and nothing revealed would look alike.
     */
    aimed?: { optionId: string; facing: RelativeFacing }[];
  },
) {
  const showAim = aimed !== undefined;
  const facingOf = (optionId: string): RelativeFacing =>
    (aimed ?? []).find((a) => a.optionId === optionId)?.facing ?? 'front';
  const [open, setOpen] = useState<OptionCardView | null>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (options.length === 0) return null;

  return (
    <div className="stack" style={{ gap: '0.3rem' }}>
      <div className="option-tray">
        {options.map((option, i) => (
          <button
            key={`${option.id}-${i}`}
            className="option-chip"
            onClick={() => setOpen(option)}
            title={payloadNote(option) || 'What does this do?'}
          >
            {option.name}
            {/* What is left of a payload, where the card has one: an
                Ablative Coat with 1 point left protects very differently
                from a fresh one. */}
            {option.remaining !== undefined && (
              <span className="option-payload num"> {option.remaining}</span>
            )}
            {/* A card is face down on this Option. Worth showing: a loaded
                Conditional is a card that can appear in any register at
                reveal, and an opponent has to be able to expect it. THAT
                there is one is public; what it is stays the holder's, and
                the holder sees their own as `heldCard` rather than this. */}
            {option.holdsCard && (
              <span className="option-on" title="Holding a card, face down">held</span>
            )}
            {/* A turn-programmed Option switched on for this turn. Shown
                only while the registers run, which is when it is doing
                something and when the programming screen -- with its own
                On/Off switch -- has left the page. */}
            {activated.includes(option.id) && (
              <span className="option-on" title="Switched on for this turn">On</span>
            )}
            {/* Which way an aimed Option is turned, on the same badge and
                for the same span: while the registers run, when the side
                it covers is what decides whether it does anything. */}
            {option.directional && showAim && (
              <span
                className="option-on"
                title={`Facing ${AIM_LABEL[facingOf(option.id)]} this turn`}
              >
                {AIM_GLYPH[facingOf(option.id)]}
              </span>
            )}
          </button>
        ))}
      </div>

      {open && (
        <div className="help-backdrop" onClick={() => setOpen(null)} role="presentation">
          <div
            className="help-panel plate option-panel"
            role="dialog"
            aria-modal="true"
            aria-label={open.name}
            tabIndex={-1}
            ref={panel}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hazard hazard-rule" role="presentation" />
            <div className="help-body">
              <div className="spread">
                <h3>{open.name}</h3>
                <button className="secondary" onClick={() => setOpen(null)}>Close</button>
              </div>

              {/* The rulebook's own classification, plus what a device
                  dropped by the Option counts as, since those tags decide
                  how it interacts with pushers, flying robots and the
                  rest. */}
              <p className="mono muted" style={{ fontSize: '0.75rem' }}>
                {timingLabel(open)}
              </p>

              <p>{open.text}</p>

              {/* What the card started with, and what happens when it runs
                  out. Not on the card itself -- no printed text mentions a
                  payload -- so it sits under the wording rather than in
                  it, where the chip's bare number has something to mean. */}
              {payloadNote(open) && (
                <p className="mono muted" style={{ fontSize: '0.75rem' }}>
                  {payloadNote(open)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
